import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search, LayoutDashboard, Users, Briefcase, GitBranch,
  Megaphone, CalendarDays, FileText, Receipt, UserCircle,
  CheckSquare, Sparkles, ArrowRight, Clock,
  Plus, User, IndianRupee, Zap,
} from 'lucide-react'
import {
  mockClients, mockDeals, mockTasks, mockCampaigns,
} from '../../data/mockData'

// ── Command types ─────────────────────────────────────────────
type CommandKind = 'nav' | 'action' | 'client' | 'deal' | 'task' | 'campaign'

interface Command {
  id:      string
  label:   string
  sub?:    string
  path:    string
  state?:  Record<string, unknown>
  icon:    React.ElementType
  kind:    CommandKind
  group:   string
  color?:  string
}

// ── Nav commands ──────────────────────────────────────────────
const NAV_COMMANDS: Command[] = [
  { id: 'nav-dashboard',  label: 'Dashboard',  path: '/dashboard',  icon: LayoutDashboard, kind: 'nav', group: 'Navigation' },
  { id: 'nav-clients',    label: 'Clients',    path: '/clients',    icon: Users,           kind: 'nav', group: 'Navigation' },
  { id: 'nav-projects',   label: 'Projects',   path: '/projects',   icon: Briefcase,       kind: 'nav', group: 'Navigation' },
  { id: 'nav-pipeline',   label: 'Pipeline',   path: '/pipeline',   icon: GitBranch,       kind: 'nav', group: 'Navigation' },
  { id: 'nav-campaigns',  label: 'Campaigns',  path: '/campaigns',  icon: Megaphone,       kind: 'nav', group: 'Navigation' },
  { id: 'nav-calendar',   label: 'Calendar',   path: '/calendar',   icon: CalendarDays,    kind: 'nav', group: 'Navigation' },
  { id: 'nav-invoices',   label: 'Invoices',   path: '/invoices',   icon: FileText,        kind: 'nav', group: 'Navigation' },
  { id: 'nav-expenses',   label: 'Expenses',   path: '/expenses',   icon: Receipt,         kind: 'nav', group: 'Navigation' },
  { id: 'nav-team',       label: 'Team',       path: '/team',       icon: UserCircle,      kind: 'nav', group: 'Navigation' },
  { id: 'nav-tasks',      label: 'Tasks',      path: '/tasks',      icon: CheckSquare,     kind: 'nav', group: 'Navigation' },
  { id: 'nav-ai-suite',   label: 'AI Suite',   path: '/ai-suite',   icon: Sparkles,        kind: 'nav', group: 'Navigation' },
]

// ── Quick action commands ─────────────────────────────────────
const ACTION_COMMANDS: Command[] = [
  { id: 'act-new-invoice',  label: 'New Invoice',   sub: 'Create invoice',    path: '/invoices',  state: { openAdd: true }, icon: FileText,    kind: 'action', group: 'Actions', color: '#ffc600' },
  { id: 'act-add-deal',     label: 'Add Deal',      sub: 'Add to pipeline',   path: '/pipeline',  state: { openAdd: true }, icon: GitBranch,   kind: 'action', group: 'Actions', color: '#e8a000' },
  { id: 'act-add-client',   label: 'Add Client',    sub: 'New client',        path: '/clients',   state: { openAdd: true }, icon: Users,       kind: 'action', group: 'Actions', color: '#ffc600' },
  { id: 'act-add-task',     label: 'New Task',      sub: 'Add to task board', path: '/tasks',     state: { openAdd: true }, icon: CheckSquare, kind: 'action', group: 'Actions', color: '#ffc600' },
  { id: 'act-add-expense',  label: 'Add Expense',   sub: 'Log an expense',    path: '/expenses',  state: { openAdd: true }, icon: Receipt,     kind: 'action', group: 'Actions', color: '#ff8c00' },
  { id: 'act-add-campaign', label: 'Launch Campaign', sub: 'New campaign',    path: '/campaigns', state: { openAdd: true }, icon: Megaphone,    kind: 'action', group: 'Actions', color: '#ff8c00' },
  { id: 'act-add-project',  label: 'New Project',    sub: 'Create project',  path: '/projects',  state: { openAdd: true }, icon: Briefcase,    kind: 'action', group: 'Actions', color: '#e8a000' },
  { id: 'act-add-event',    label: 'Add Event',      sub: 'Add to calendar', path: '/calendar',  state: { openAdd: true }, icon: CalendarDays, kind: 'action', group: 'Actions', color: '#ffc600' },
  { id: 'act-add-member',   label: 'Add Team Member', sub: 'Invite someone', path: '/team',      state: { openAdd: true }, icon: UserCircle,   kind: 'action', group: 'Actions', color: '#a0a0a0' },
]

// ── Entity commands built from mock data ──────────────────────
const CLIENT_COMMANDS: Command[] = mockClients.map(c => ({
  id:    'client-' + c.id,
  label: c.name,
  sub:   `Client · ${c.contact}`,
  path:  '/clients',
  icon:  User,
  kind:  'client' as CommandKind,
  group: 'Clients',
  color: c.status === 'active' ? '#32c864' : '#555',
}))

const DEAL_COMMANDS: Command[] = mockDeals
  .filter(d => d.stage !== 'closed-lost')
  .map(d => ({
    id:    'deal-' + d.id,
    label: d.title,
    sub:   `${d.company} · ₹${d.value.toLocaleString('en-IN')}`,
    path:  '/pipeline',
    icon:  GitBranch,
    kind:  'deal' as CommandKind,
    group: 'Deals',
  }))

const TASK_COMMANDS: Command[] = mockTasks
  .filter(t => t.status !== 'done')
  .slice(0, 20)
  .map(t => ({
    id:    'task-' + t.id,
    label: t.title,
    sub:   `${t.assignee} · ${t.priority}`,
    path:  '/tasks',
    icon:  CheckSquare,
    kind:  'task' as CommandKind,
    group: 'Tasks',
    color: t.priority === 'urgent' ? '#cc3300' : t.priority === 'high' ? '#ff8c00' : '#555',
  }))

const CAMPAIGN_COMMANDS: Command[] = mockCampaigns
  .filter(c => c.status === 'active')
  .slice(0, 10)
  .map(c => ({
    id:    'camp-' + c.id,
    label: c.name,
    sub:   `${c.type} · ROAS ${c.roas.toFixed(1)}x`,
    path:  '/campaigns',
    icon:  Megaphone,
    kind:  'campaign' as CommandKind,
    group: 'Campaigns',
  }))

const ALL_ENTITY_COMMANDS = [...CLIENT_COMMANDS, ...DEAL_COMMANDS, ...TASK_COMMANDS, ...CAMPAIGN_COMMANDS]
const ALL_COMMANDS = [...NAV_COMMANDS, ...ACTION_COMMANDS, ...ALL_ENTITY_COMMANDS]

// ── localStorage recents ──────────────────────────────────────
const RECENTS_KEY = 'cb_cmd_recents'
function getRecents(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]') } catch { return [] }
}
function saveRecent(id: string) {
  const prev = getRecents().filter(r => r !== id)
  localStorage.setItem(RECENTS_KEY, JSON.stringify([id, ...prev].slice(0, 6)))
}

// ── Group priority for display ────────────────────────────────
const GROUP_ORDER = ['Actions', 'Clients', 'Deals', 'Tasks', 'Campaigns', 'Navigation', 'Recent']

interface CommandPaletteProps { open: boolean; onClose: () => void }

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery]         = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const navigate                  = useNavigate()
  const inputRef                  = useRef<HTMLInputElement>(null)
  const listRef                   = useRef<HTMLDivElement>(null)

  const q = query.trim().toLowerCase()

  // ── Build display list ────────────────────────────────────────
  const displayList: Command[] = (() => {
    if (!q) {
      // No query → show recents + actions
      const recents = getRecents()
      const recentCmds = recents
        .map(id => ALL_COMMANDS.find(c => c.id === id))
        .filter(Boolean) as Command[]
      const actionCmds = ACTION_COMMANDS

      if (recentCmds.length) {
        // Mark recents with 'Recent' group for display
        const tagged = recentCmds.map(c => ({ ...c, group: 'Recent' }))
        return [...tagged, ...actionCmds]
      }
      return [...actionCmds, ...NAV_COMMANDS]
    }

    // With query → search all
    return ALL_COMMANDS.filter(c =>
      c.label.toLowerCase().includes(q) ||
      (c.sub ?? '').toLowerCase().includes(q) ||
      c.group.toLowerCase().includes(q)
    )
  })()

  // Focus on open
  useEffect(() => {
    if (open) { setQuery(''); setActiveIdx(0); setTimeout(() => inputRef.current?.focus(), 30) }
  }, [open])

  // Reset active when list changes
  useEffect(() => { setActiveIdx(0) }, [query])

  // Scroll active into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`) as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  const runCommand = (cmd: Command) => {
    saveRecent(cmd.id)
    navigate(cmd.path, cmd.state ? { state: cmd.state } : undefined)
    onClose()
  }

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, displayList.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); const cmd = displayList[activeIdx]; if (cmd) runCommand(cmd) }
    else if (e.key === 'Escape') onClose()
  }, [displayList, activeIdx])

  if (!open) return null

  // Group display list (maintaining order)
  const groups: { name: string; cmds: Command[] }[] = []
  const groupMap: Record<string, Command[]> = {}
  displayList.forEach(cmd => {
    if (!groupMap[cmd.group]) { groupMap[cmd.group] = []; groups.push({ name: cmd.group, cmds: groupMap[cmd.group] }) }
    groupMap[cmd.group].push(cmd)
  })
  groups.sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a.name)
    const bi = GROUP_ORDER.indexOf(b.name)
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

  let globalIdx = 0

  const kindIcon: Record<CommandKind, string> = {
    nav: '#555', action: '#ffc600', client: '#ffc600', deal: '#e8a000', task: '#555', campaign: '#ff8c00'
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[100]"
        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
        onClick={onClose} />

      {/* Palette */}
      <div className="fixed z-[101] left-1/2 rounded-2xl overflow-hidden"
        style={{ top: '15vh', transform: 'translateX(-50%)', width: '100%', maxWidth: 580,
          background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.3)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)' }}>

        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b" style={{ borderColor: 'rgba(255,198,0,0.12)' }}>
          <Search size={16} style={{ color: '#555', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, clients, deals, tasks…"
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder-gray-600"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-xs px-1.5 py-0.5 rounded"
              style={{ color: '#555', border: '1px solid #2a2a2a', background: '#1a1a1a' }}>
              clear
            </button>
          )}
          <kbd className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#1a1a1a', color: '#555', border: '1px solid #2a2a2a' }}>ESC</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-96 overflow-y-auto py-2">
          {displayList.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" style={{ color: '#555' }}>
              No results for "{query}"
            </div>
          ) : groups.map(({ name, cmds }) => (
            <div key={name}>
              {/* Group header */}
              <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                {name === 'Recent' && <Clock size={10} style={{ color: '#444' }} />}
                {name === 'Actions' && <Plus size={10} style={{ color: '#444' }} />}
                {name === 'Clients' && <Users size={10} style={{ color: '#444' }} />}
                {name === 'Deals' && <GitBranch size={10} style={{ color: '#444' }} />}
                {name === 'Tasks' && <CheckSquare size={10} style={{ color: '#444' }} />}
                {name === 'Campaigns' && <Megaphone size={10} style={{ color: '#444' }} />}
                {name === 'Navigation' && <Zap size={10} style={{ color: '#444' }} />}
                <span className="text-xs font-semibold tracking-wide" style={{ color: '#444' }}>{name.toUpperCase()}</span>
                <span className="text-xs ml-auto" style={{ color: '#333' }}>{cmds.length}</span>
              </div>

              {cmds.map(cmd => {
                const idx = globalIdx++
                const isActive = idx === activeIdx
                const Icon = cmd.icon
                const iconColor = cmd.color ?? kindIcon[cmd.kind]
                return (
                  <div
                    key={cmd.id}
                    data-idx={idx}
                    className="flex items-center gap-3 mx-2 px-3 py-2 rounded-lg cursor-pointer transition-all"
                    style={{
                      background: isActive ? 'rgba(255,198,0,0.1)' : 'transparent',
                      border: isActive ? '1px solid rgba(255,198,0,0.2)' : '1px solid transparent',
                    }}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => runCommand(cmd)}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: isActive ? 'rgba(255,198,0,0.12)' : '#1a1a1a' }}>
                      <Icon size={13} style={{ color: isActive ? '#ffc600' : iconColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate" style={{ color: isActive ? '#fff' : '#d0d0d0' }}>{cmd.label}</p>
                      {cmd.sub && (
                        <p className="text-xs truncate mt-0.5" style={{ color: '#555' }}>{cmd.sub}</p>
                      )}
                    </div>
                    {cmd.kind === 'action' && (
                      <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: 'rgba(255,198,0,0.1)', color: '#e8a000' }}>
                        action
                      </span>
                    )}
                    {isActive && cmd.kind !== 'action' && (
                      <ArrowRight size={12} style={{ color: '#ffc600', flexShrink: 0 }} />
                    )}
                    {isActive && cmd.kind === 'action' && (
                      <ArrowRight size={12} style={{ color: '#ffc600', flexShrink: 0 }} />
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t" style={{ borderColor: 'rgba(255,198,0,0.08)' }}>
          {[['↑↓', 'Navigate'], ['↵', 'Open'], ['Esc', 'Close']].map(([key, label]) => (
            <span key={key} className="flex items-center gap-1.5 text-xs" style={{ color: '#444' }}>
              <kbd className="px-1.5 py-0.5 rounded text-xs" style={{ background: '#1a1a1a', color: '#555', border: '1px solid #2a2a2a' }}>{key}</kbd>
              {label}
            </span>
          ))}
          <span className="ml-auto text-xs" style={{ color: '#333' }}>
            {displayList.length} result{displayList.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </>
  )
}
